import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Box, Typography } from "@mui/material";
import { getCalllogsAction } from "../../ProjectSection/CampaignSection/Store/Campaign.action";
import CommonModal from "../../CommonComponents/Modal/CommonModal";
import moment from "moment";
import "../../../../assets/css/page/CallLogs.scss";

function parseLogMessage(raw: string | object): {
  message: string;
  logTime?: string;
  color?: string;
} {
  if (raw == null) return { message: "" };
  let parsed: Record<string, unknown>;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { message: raw };
    }
  } else if (typeof raw === "object" && raw !== null) {
    parsed = raw as Record<string, unknown>;
  } else {
    return { message: String(raw) };
  }
  return {
    message: (parsed?.message as string) ?? (typeof raw === "string" ? raw : ""),
    logTime: parsed?.log_time as string | undefined,
    color: parsed?.color as string | undefined,
  };
}

/** Use a visible color on dark background; map black/dark API colors to light gray */
const DEFAULT_LOG_COLOR = "#d4d4d4";
const DARK_NAMED_COLORS = [
  "black",
  "navy",
  "darkblue",
  "darkgrey",
  "darkgray",
  "darkslategrey",
  "darkslategray",
  "dimgray",
  "grey",
  "gray",
];

function getReadableLogColor(apiColor: string | undefined): string {
  if (apiColor == null || apiColor === "") return DEFAULT_LOG_COLOR;
  const normalized = String(apiColor).trim().toLowerCase();
  if (DARK_NAMED_COLORS.some((c) => normalized === c || normalized === c.replace(/\s/g, "")))
    return DEFAULT_LOG_COLOR;
  const hexMatch = normalized.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hexMatch) {
    let r = 0,
      g = 0,
      b = 0;
    const h = hexMatch[1];
    if (h.length === 3) {
      r = parseInt(h[0] + h[0], 16);
      g = parseInt(h[1] + h[1], 16);
      b = parseInt(h[2] + h[2], 16);
    } else {
      r = parseInt(h.slice(0, 2), 16);
      g = parseInt(h.slice(2, 4), 16);
      b = parseInt(h.slice(4, 6), 16);
    }
    if (r + g + b < 200) return DEFAULT_LOG_COLOR;
  }
  return apiColor;
}

function CallLogs({ call_uuid, show, toggle }) {
  const dispatch = useDispatch();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!show || !call_uuid) return;
    const init = async () => {
      setLoading(true);
      try {
        const res = await dispatch(getCalllogsAction(call_uuid));
        setData(Array.isArray(res) ? res : []);
      } catch {
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    init();

    return () => {
      setData([]);
    };
  }, [show, call_uuid]);

  return (
    <CommonModal
      show={show}
      heading="Logs"
      body={
        <Box className="call-logs-view">
          {loading ? (
            <Typography variant="body2" color="text.secondary">
              Loading logs...
            </Typography>
          ) : data?.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No logs available
            </Typography>
          ) : (
            <Box className="call-logs-view__list" component="pre">
              {data.map((entry, index) => {
                const {
                  message: displayMessage,
                  logTime,
                  color: lineColor,
                } = parseLogMessage(entry?.message);
                const rawTs = entry?.timestamp ?? entry?.ingestionTime;
                const timeStr = (() => {
                  const formatLocal = "YYYY-MM-DD HH:mm:ss.SSS";
                  if (logTime) {
                    const parsed = moment.utc(logTime);
                    return parsed.isValid()
                      ? parsed.local().format(formatLocal)
                      : logTime;
                  }
                  if (rawTs != null) {
                    return moment(rawTs).format(formatLocal);
                  }
                  return "";
                })();
                const lineColorStyle: React.CSSProperties = {
                  color: getReadableLogColor(lineColor),
                };
                return (
                  <div
                    key={index}
                    className="call-logs-view__line"
                    style={lineColorStyle}
                    data-log-color={lineColor || undefined}
                  >
                    <span className="call-logs-view__time">{timeStr}</span>
                    {rawTs != null && (
                      <span className="call-logs-view__timestamp">
                        ({rawTs})
                      </span>
                    )}
                    <span className="call-logs-view__msg">{displayMessage}</span>
                  </div>
                );
              })}
            </Box>
          )}
        </Box>
      }
      toggle={toggle}
      size={1400}
      handleSave={false}
      footer={false}
      customClassName="call-logs-modal"
    />
  );
}

export default CallLogs;
